import * as THREE from 'three';
import { DEFAULT_AO_SETTINGS, validateAOSettings } from './ambientOcclusion.js';

export const RENDER_VIEWS = [
  ['front', 'Front'], ['back', 'Rear'], ['left', 'Left ¾'], ['right', 'Right ¾'], ['custom', 'Custom angle']
];
export const DEFAULT_RENDER = { width: 1600, height: 2000, view: 'front', yawDeg: -30,
  elevationDeg: 10, paddingPct: 8, angleDeg: 0, passiveAngleDeg: 0,
  background: 'transparent', format: 'png', ambientOcclusion: false, aoSettings: DEFAULT_AO_SETTINGS, antialias: 'smaa', environment: 'studio', environmentIntensity: 0.55 };

export function renderPixelSize(settings) {
  const scale = settings.antialias === 'ssaa2' ? 2 : 1;
  const width = settings.width * scale, height = settings.height * scale;
  if (width > 8192 || height > 8192 || width * height > 16777216) {
    throw new Error('SSAA 2× supports up to 4.2 MP output. Use SMAA Ultra for larger images.');
  }
  return { width, height, scale };
}

export function validateRenderSettings(s) {
  if (!s || ![s.width, s.height].every((n) => Number.isInteger(n) && n >= 64 && n <= 8192) ||
      s.width * s.height > 16777216) throw new Error('Resolution: 64–8192 px per side, up to 16.8 megapixels.');
  if (!RENDER_VIEWS.some(([id]) => id === s.view)) throw new Error('Unknown camera view.');
  for (const [key, min, max] of [['yawDeg', -180, 180], ['elevationDeg', -60, 60], ['paddingPct', 2, 35],
    ['angleDeg', 0, 110], ['passiveAngleDeg', 0, 110]]) {
    if (!Number.isFinite(s[key]) || s[key] < min || s[key] > max) throw new Error(`${key}: allowed range ${min}–${max}.`);
  }
  if (!['png', 'jpeg'].includes(s.format)) throw new Error('Choose PNG or JPG.');
  if (s.ambientOcclusion !== undefined && typeof s.ambientOcclusion !== 'boolean') throw new Error('AO must be on or off.');
  validateAOSettings(s.aoSettings);
  if (s.antialias !== undefined && !['msaa', 'smaa', 'ssaa2'].includes(s.antialias)) throw new Error('Unknown antialiasing option.');
  if (s.environment !== undefined && !['daylight', 'studio'].includes(s.environment) && !/^custom:[0-9a-f-]{36}$/.test(s.environment)) throw new Error('Unknown HDRI option.');
  if (s.environmentIntensity !== undefined && (!Number.isFinite(s.environmentIntensity) || s.environmentIntensity < 0 || s.environmentIntensity > 3)) throw new Error('HDRI brightness must be 0–3.');
  renderPixelSize(s);
  if (s.background !== 'transparent' && !/^#[0-9a-f]{6}$/i.test(s.background)) throw new Error('Background must be transparent or a HEX colour.');
  if (s.format === 'jpeg' && s.background === 'transparent') throw new Error('JPG requires a background colour. Use PNG for transparency.');
  return s;
}

/** Fit every corner of the open assembly into an orthographic product shot. */
export function createRenderCamera(box, settings) {
  validateRenderSettings(settings);
  if (box.isEmpty()) throw new Error('No model available to render.');
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const yaw = settings.view === 'custom' ? settings.yawDeg
    : ({ front: 0, back: 180, left: -30, right: 30 })[settings.view];
  const elevation = ['front', 'back'].includes(settings.view) ? 0 : settings.elevationDeg;
  // Exterior/front is -Z in the source model; yaw is relative to that face.
  const theta = THREE.MathUtils.degToRad(yaw + 180), phi = THREE.MathUtils.degToRad(elevation);
  const direction = new THREE.Vector3(Math.sin(theta) * Math.cos(phi), Math.sin(phi), Math.cos(theta) * Math.cos(phi));
  const distance = size.length() * 2 + 1;
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, distance + size.length() * 2);
  camera.position.copy(center).addScaledVector(direction, distance); camera.lookAt(center); camera.updateMatrixWorld(true);
  const projected = new THREE.Box3();
  for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) {
    projected.expandByPoint(new THREE.Vector3(x, y, z).applyMatrix4(camera.matrixWorldInverse));
  }
  const pad = 1 + settings.paddingPct / 100 * 2;
  const aspect = settings.width / settings.height;
  const halfH = Math.max((projected.max.y - projected.min.y) / 2,
    (projected.max.x - projected.min.x) / 2 / aspect) * pad;
  camera.left = -halfH * aspect; camera.right = halfH * aspect;
  camera.top = halfH; camera.bottom = -halfH; camera.updateProjectionMatrix();
  return camera;
}
